/* eslint-disable */
import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { useNavigate, useLocation } from "react-router-dom";
import { FiChevronLeft } from "react-icons/fi";

import config from "../config/auth.config";
import { startPassVerification } from "../providers/api/identityStub"; // 서버 요청 스텁
import { getAuth, signInAnonymously } from "firebase/auth";

import * as usersService from "services/usersService";
import * as membersService from "services/membersService";

/**
 * PhonePassPage (PASS 본인확인용, reCAPTCHA 제거 버전)
 * -------------------------------------------------------
 * - 서버에서 PASS 인증 진행 후 익명 로그인(uid 생성)
 * - users.phoneE164 저장 + members 존재 여부 판단
 * - 멤버 있으면 홈, 없으면 RegisterPage로 이동
 */
export default function PhonePassPage() {
    const nav = useNavigate();
    const loc = useLocation();
    const sp = new URLSearchParams(loc.search);
    const returnTo = sp.get("returnTo") || null;
    const fromPath = `${loc.pathname}${loc.search || ""}`;
    const OTP_TTL = config?.phoneAuth?.otpTtlSec ?? 180;

    const [step, setStep] = useState("input");
    const [rawPhone, setRawPhone] = useState("");
    const [e164, setE164] = useState("");
    const [otp, setOtp] = useState("");
    const [remain, setRemain] = useState(0);
    const [err, setErr] = useState("");
    const [debugCode, setDebugCode] = useState("");

    /* 타이머 */
    useEffect(() => {
        if (step !== "otp" || remain <= 0) return;
        const t = setInterval(() => setRemain((s) => (s > 0 ? s - 1 : 0)), 1000);
        return () => clearInterval(t);
    }, [step, remain]);

    /* 포맷 유틸 */
    const formatPhoneKR = (v = "") => {
        const d = v.replace(/[^\d]/g, "");
        if (d.length <= 3) return d;
        if (d.length <= 7) return `${d.slice(0, 3)}-${d.slice(3)}`;
        return `${d.slice(0, 3)}-${d.slice(3, 7)}-${d.slice(7, 11)}`;
    };
    const normalizeE164KR = (v) => {
        const n = String(v || "").replace(/[^\d]/g, "");
        if (n.startsWith("010")) return `+82${n.slice(1)}`;
        if (n.startsWith("82")) return `+${n}`;
        return v.startsWith("+") ? v : v;
    };
    const isValidE164 = (p) => /^\+82(10\d{8})$/.test(p);
    const genCode = () => String(Math.floor(100000 + Math.random() * 900000));

    /* 뒤로가기 */
    const handleBack = () => {
        if (step === "otp") {
            setStep("input");
            setRemain(0);
            setOtp("");
            setErr("");
            setDebugCode("");
        } else if (window.history.length > 1) nav(-1);
        else nav(config?.postSignOutRoute || "/loginlanding", { replace: true });
    };

    /* 인증 시작 (PASS 요청) */
    const handleStart = async (e) => {
        e?.preventDefault?.();
        setErr("");
        const p = normalizeE164KR(rawPhone);
        if (!isValidE164(p)) return setErr("전화번호 형식을 확인해 주세요.");
        setE164(p);

        try {
            await startPassVerification({ returnUrl: window.location.href }); // 서버 PASS 요청
            const code = genCode(); // 로컬 테스트용 코드
            setDebugCode(code);
            setOtp("");
            setRemain(OTP_TTL);
            setStep("otp");
        } catch (e) {
            console.error("[pass][start]", e);
            setErr("PASS 인증을 시작할 수 없어요. 잠시 후 다시 시도해 주세요.");
        }
    };

    /* 인증 완료 처리 */
    const handleVerify = async (e) => {
        e?.preventDefault?.();
        setErr("");

        const code = (otp || "").replace(/\D/g, "");
        if (code.length !== 6) return setErr("6자리 인증코드를 입력해 주세요.");
        if (remain <= 0) return setErr("인증 시간이 만료되었어요. 처음부터 다시 시도해 주세요.");
        if (code !== debugCode) return setErr("인증코드가 올바르지 않습니다."); // 실제는 서버 검증 결과로 대체

        try {
            // 1️⃣ Firebase 익명 로그인 (uid 확보)
            const auth = getAuth();
            let u = auth.currentUser;
            if (!u) {
                const { user } = await signInAnonymously(auth);
                u = user;
            }

            // 2️⃣ users 문서에 전화번호 저장
            await usersService.ensureUserDoc(u.uid, {
                provider: "phone",
                phoneE164: e164,
                loginMethodToSet: "phone",
            });

            // 3️⃣ members 존재 여부 확인 (전화번호 기준)
            const hasMember = await membersService.existsMemberByPhone(e164);

            // 4️⃣ 다음 경로 결정
     
            
            if (hasMember) {
                nav("/home", { replace: true });
             } else {
                nav("/register", { replace: true });
            }
            
      
        } catch (e2) {
            console.error("[pass][verify]", e2);
            setErr("인증에 실패했어요. 다시 시도해 주세요.");
        }
    };

    const mm = String(Math.floor(remain / 60));
    const ss = String(remain % 60).padStart(2, "0");

    return (
        <Page>
            <Card>
                <TopRow>
                    <BackBtn type="button" onClick={handleBack} aria-label="뒤로가기">
                        <FiChevronLeft size={20} />
                        <span>뒤로</span>
                    </BackBtn>
                </TopRow>

                <Head>
                    <Title>PASS 본인확인</Title>
                    <Hint>본인 명의 휴대전화로 안전하게 인증합니다.</Hint>
                </Head>

                {step === "input" && (
                    <form onSubmit={handleStart}>
                        <Field>
                            <span>전화번호</span>
                            <PhoneInput
                                inputMode="tel"
                                autoComplete="tel"
                                placeholder="010-1234-5678"
                                value={rawPhone}
                                onChange={(e) => setRawPhone(formatPhoneKR(e.target.value))}
                            />
                        </Field>
                        {err && <Err>{err}</Err>}
                        <Primary
                            type="submit"
                            disabled={!isValidE164(normalizeE164KR(rawPhone))}
                        >
                            PASS로 본인확인
                        </Primary>
                        <Sub>인증을 시작하면 3분 이내에 인증코드를 입력해야 합니다.</Sub>
                    </form>
                )}

                {step === "otp" && (
                    <form onSubmit={handleVerify}>
                        <DebugBox aria-live="polite">
                            테스트용 코드입니다. 아래 코드를 입력해 진행하세요.
                            <CodePill
                                onClick={() => navigator.clipboard?.writeText(debugCode)}
                            >
                                {debugCode}
                            </CodePill>
                        </DebugBox>

                        <Field>
                            <span>인증코드</span>
                            <CodeInput
                                inputMode="numeric"
                                maxLength={6}
                                placeholder="6자리 인증코드"
                                value={otp}
                                onChange={(e) =>
                                    setOtp(e.target.value.replace(/[^\d]/g, ""))
                                }
                                autoFocus
                            />
                            <TimerRow>
                                남은 시간 <strong>{mm}:{ss}</strong>
                            </TimerRow>
                        </Field>

                        {err && <Err>{err}</Err>}
                        <Primary
                            type="submit"
                            disabled={
                                otp.replace(/\D/g, "").length !== 6 || remain <= 0
                            }
                        >
                            확인
                        </Primary>
                        <Sub>정식 연동 전 임시 PASS 인증 플로우입니다.</Sub>
                    </form>
                )}
            </Card>
        </Page>
    );
}

/* styled-components */
const Page = styled.main`
  min-height: 100dvh;
  background: var(--color-bg, #faf7f3);
  color: #111;
`;
const Card = styled.section`
  width: 100%;
  max-width: 560px;
  min-height: 100dvh;
  margin: 0 auto;
  background: #fff;
  border: 1px solid var(--color-border, #eee3d9);
  border-radius: 16px;
  box-shadow: 0 10px 30px rgba(0,0,0,.06);
  display: flex;
  flex-direction: column;
  padding: 16px;
  @media (min-width: 641px) { padding: 20px 24px; }
`;
const TopRow = styled.div`display:flex; align-items:center; margin-bottom:8px;`;
const BackBtn = styled.button`
  display:inline-grid; grid-auto-flow:column; gap:6px; align-items:center;
  border:1px solid var(--color-border,#e6e0d8); background:#fff; border-radius:10px;
  height:34px; padding:0 10px; font-weight:800; font-size:13px; cursor:pointer;
  &:active{ transform:translateY(1px); }
`;
const Head = styled.header`margin-bottom:12px;`;
const Title = styled.h1`font-size:18px;font-weight:900;margin:0 0 6px;`;
const Hint = styled.p`margin:0;color:#666;font-size:13px;`;
const Field = styled.label`display:grid;gap:6px;margin:10px 0 4px;font-size:13px;`;
const PhoneInput = styled.input`
  height:48px;border-radius:12px;border:1px solid var(--color-border);
  padding:0 14px;font-size:16px;text-align:center;letter-spacing:1px;
  ::placeholder{color:rgba(0,0,0,0.28);}
  &:focus{border-color:var(--color-primary,#d9a679);
    box-shadow:0 0 0 3px rgba(217,166,121,.25);}
`;
const CodeInput = styled(PhoneInput)`
  letter-spacing:6px;font-weight:800;
  ::placeholder{color:rgba(0,0,0,0.22);}
`;
const TimerRow = styled.div`margin-top:6px;font-size:12px;color:#666;`;
const Err = styled.div`color:#e03131;font-size:12px;margin-top:4px;`;
const Primary = styled.button`
  height:46px;width:100%;border:none;border-radius:12px;
  background:var(--color-accent,#E6C39E);color:#fff;font-weight:800;font-size:15px;
  margin-top:6px;&:disabled{opacity:.6;}
`;
const Sub = styled.p`margin:8px 0 0;font-size:12px;color:#777;`;
const DebugBox = styled.div`
  background:#fff7e6;border:1px solid #ffe0a3;color:#7A4A00;
  border-radius:12px;padding:10px 12px;font-size:12px;line-height:1.5;margin:0 0 10px;
`;
const CodePill = styled.button`
  margin-left:8px;border:1px dashed #d9a679;background:#fff;color:#5a3a2e;
  font-weight:900;letter-spacing:2px;padding:2px 8px;border-radius:8px;cursor:pointer;
`;
